import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '../Common/Tabs';
import { ExternalLink, FileText, CheckCircle, XCircle, AlertTriangle, MessageSquare, User, Calendar } from 'lucide-react';

interface ContextPanelProps {
  jiraTicket?: any;
  pseudocodeRef?: string;
  testResults?: any;
  comments: Array<{
    id: string;
    author: string;
    content: string;
    createdAt: string;
  }>;
  className?: string;
}

const ContextPanel: React.FC<ContextPanelProps> = ({
  jiraTicket,
  pseudocodeRef,
  testResults,
  comments,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState<'jira' | 'pseudocode' | 'tests' | 'comments'>('jira');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTestStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'passed':
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      <div className="border-b">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
          <TabsList className="w-full justify-start px-4 py-2">
            <TabsTrigger value="jira">Jira Ticket</TabsTrigger>
            <TabsTrigger value="pseudocode">Pseudocode</TabsTrigger>
            <TabsTrigger value="tests">Test Results</TabsTrigger>
            <TabsTrigger value="comments">Comments ({comments.length})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'jira' && (
          <div className="p-4">
            {jiraTicket ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {jiraTicket.key}: {jiraTicket.summary}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        jiraTicket.status?.name === 'Done' 
                          ? 'bg-green-100 text-green-800'
                          : jiraTicket.status?.name === 'In Progress'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {jiraTicket.status?.name || 'Unknown'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        jiraTicket.priority?.name === 'High'
                          ? 'bg-red-100 text-red-800'
                          : jiraTicket.priority?.name === 'Medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {jiraTicket.priority?.name || 'Low'} Priority
                      </span>
                    </div>
                  </div>
                  {jiraTicket.url && (
                    <a
                      href={jiraTicket.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="View in Jira"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>

                {jiraTicket.description && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {jiraTicket.description}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  {jiraTicket.assignee && (
                    <div>
                      <span className="font-medium text-gray-900">Assignee:</span>
                      <div className="flex items-center mt-1">
                        <User className="w-4 h-4 text-gray-500 mr-1" />
                        <span className="text-gray-700">{jiraTicket.assignee.displayName}</span>
                      </div>
                    </div>
                  )}

                  {jiraTicket.reporter && (
                    <div>
                      <span className="font-medium text-gray-900">Reporter:</span>
                      <div className="flex items-center mt-1">
                        <User className="w-4 h-4 text-gray-500 mr-1" />
                        <span className="text-gray-700">{jiraTicket.reporter.displayName}</span>
                      </div>
                    </div>
                  )}

                  {jiraTicket.created && (
                    <div>
                      <span className="font-medium text-gray-900">Created:</span>
                      <div className="flex items-center mt-1">
                        <Calendar className="w-4 h-4 text-gray-500 mr-1" />
                        <span className="text-gray-700">{formatDate(jiraTicket.created)}</span>
                      </div>
                    </div>
                  )}

                  {jiraTicket.updated && (
                    <div>
                      <span className="font-medium text-gray-900">Updated:</span>
                      <div className="flex items-center mt-1">
                        <Calendar className="w-4 h-4 text-gray-500 mr-1" />
                        <span className="text-gray-700">{formatDate(jiraTicket.updated)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {jiraTicket.labels && jiraTicket.labels.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-900 block mb-2">Labels:</span>
                    <div className="flex flex-wrap gap-2">
                      {jiraTicket.labels.map((label: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No Jira ticket information available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pseudocode' && (
          <div className="p-4">
            {pseudocodeRef ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Pseudocode Reference</h3>
                  <button className="text-blue-600 hover:text-blue-800 transition-colors">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                    {pseudocodeRef}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No pseudocode reference available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tests' && (
          <div className="p-4">
            {testResults ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-600">
                      {testResults.passed || 0}
                    </div>
                    <div className="text-sm text-green-800">Passed</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-red-600">
                      {testResults.failed || 0}
                    </div>
                    <div className="text-sm text-red-800">Failed</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-2xl font-bold text-blue-600">
                      {testResults.total || 0}
                    </div>
                    <div className="text-sm text-blue-800">Total</div>
                  </div>
                </div>

                {testResults.coverage && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">Code Coverage</span>
                      <span className="text-sm font-medium text-gray-700">
                        {testResults.coverage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          testResults.coverage >= 80 
                            ? 'bg-green-500' 
                            : testResults.coverage >= 60 
                            ? 'bg-yellow-500' 
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${testResults.coverage}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {testResults.results && testResults.results.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Test Results</h4>
                    {testResults.results.map((result: any, index: number) => (
                      <div key={index} className="flex items-start space-x-2 p-2 bg-gray-50 rounded">
                        {getTestStatusIcon(result.status)}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {result.name}
                          </div>
                          {result.message && (
                            <div className="text-xs text-gray-600 mt-1">
                              {result.message}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {result.duration && `${result.duration}ms`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No test results available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="p-4">
            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-900">{comment.author}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDate(comment.createdAt)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {comment.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No comments yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextPanel;
